import { useState, useRef, useEffect } from 'react'
import { Search, X, Loader2, MapPin } from 'lucide-react'
import mapboxgl from 'mapbox-gl'
import { googlePlacesService } from '../../services/googlePlacesService'

interface LocationSearchControlProps {
  map: mapboxgl.Map | null
  className?: string
}

interface SearchResult {
  placeId: string
  name: string
  address: string
  lat?: number
  lng?: number
}

export function LocationSearchControl({ map, className = '' }: LocationSearchControlProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [error, setError] = useState<string | null>(null)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const searchPopupRef = useRef<mapboxgl.Popup | null>(null)


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        // Remove popup when closing
        if (searchPopupRef.current) {
          searchPopupRef.current.remove()
          searchPopupRef.current = null
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Focus input when opening
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  // Clean up popup when component unmounts
  useEffect(() => {
    return () => {
      if (searchPopupRef.current) {
        searchPopupRef.current.remove()
      }
    }
  }, [])

  const handleSearch = async (query: string) => {
    if (!query.trim() || !map) {
      setSearchResults([])
      return
    }

    setIsSearching(true)
    setError(null)

    try {
      const results = await googlePlacesService.searchLocation(query)
      
      const formattedResults: SearchResult[] = results.map(result => ({
        placeId: result.place_id,
        name: result.name || result.formatted_address?.split(',')[0] || 'Location',
        address: result.formatted_address || '',
        lat: result.geometry?.location?.lat,
        lng: result.geometry?.location?.lng
      }))

      setSearchResults(formattedResults)
    } catch (err) {
      console.error('Location search error:', err)
      setError('Failed to search location')
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }

  const handleQueryChange = (query: string) => {
    setSearchQuery(query)
    
    // Cancel previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      handleSearch(query)
    }, 500)
  }

  const handleSelectLocation = async (result: SearchResult) => {
    if (!map) return

    let selectedResult = result

    if (selectedResult.lat == null || selectedResult.lng == null) {
      setIsSearching(true)
      setError(null)

      try {
        const details = await googlePlacesService.getLocationDetails(result.placeId)

        if (!details?.geometry?.location) {
          throw new Error('Selected location has no coordinates')
        }

        selectedResult = {
          ...result,
          name: details.name || result.name,
          address: details.formatted_address || result.address,
          lat: details.geometry.location.lat,
          lng: details.geometry.location.lng
        }
      } catch (err) {
        console.error('Location details error:', err)
        setError('Failed to load selected location')
        return
      } finally {
        setIsSearching(false)
      }
    }

    if (selectedResult.lat == null || selectedResult.lng == null) {
      setError('Selected location has no coordinates')
      return
    }

    // Remove existing popup if any
    if (searchPopupRef.current) {
      searchPopupRef.current.remove()
    }

    // Create a styled popup with location info
    searchPopupRef.current = new mapboxgl.Popup({ 
      closeButton: false,
      closeOnClick: false,
      className: 'search-location-popup',
      maxWidth: 'none'
    })
      .setLngLat([selectedResult.lng, selectedResult.lat])
      .setHTML(`
        <div style="
          background: rgba(31, 41, 55, 0.95);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 16px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
          min-width: 250px;
        ">
          <div style="display: flex; align-items: start; gap: 12px;">
            <div style="
              background-color: #10b981;
              width: 32px;
              height: 32px;
              border-radius: 8px;
              display: flex;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;
            ">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
            </div>
            <div style="flex: 1;">
              <h3 style="
                margin: 0 0 4px 0;
                font-weight: 600;
                font-size: 15px;
                color: #fff;
                line-height: 1.2;
              ">${selectedResult.name}</h3>
              <p style="
                margin: 0;
                font-size: 13px;
                color: #9ca3af;
                line-height: 1.4;
              ">${selectedResult.address}</p>
            </div>
          </div>
        </div>
      `)
      .addTo(map)

    // Add CSS for popup arrow styling (only once)
    if (!document.getElementById('search-popup-styles')) {
      const style = document.createElement('style')
      style.id = 'search-popup-styles'
      style.textContent = `
        .search-location-popup .mapboxgl-popup-content {
          padding: 0 !important;
          background: transparent !important;
          box-shadow: none !important;
        }
        .search-location-popup .mapboxgl-popup-tip {
          border-top-color: rgba(31, 41, 55, 0.95) !important;
        }
      `
      document.head.appendChild(style)
    }

    // Fly to the selected location
    map.flyTo({
      center: [selectedResult.lng, selectedResult.lat],
      zoom: 14,
      duration: 2000,
      essential: true
    })

    // Remove popup after 8 seconds
    setTimeout(() => {
      if (searchPopupRef.current) {
        searchPopupRef.current.remove()
        searchPopupRef.current = null
      }
    }, 8000)

    // Clear search and close
    setSearchQuery('')
    setSearchResults([])
    setIsOpen(false)
  }

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Search Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="text-gray-200 hover:bg-gray-700/50 transition-colors flex items-center justify-center w-full h-[32px]"
        title="Search location"
      >
        <Search className="w-4 h-4" />
      </button>

      {/* Search Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-gray-800/95 backdrop-blur-md rounded-lg shadow-xl border border-gray-700/50 overflow-hidden">
          {/* Search Input */}
          <div className="p-3 border-b border-gray-700/50">
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => handleQueryChange(e.target.value)}
                placeholder="Search for a location..."
                className="w-full pl-9 pr-9 py-2 bg-gray-900/50 border border-gray-700 rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery('')
                    setSearchResults([])
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Search Results */}
          <div className="max-h-64 overflow-y-auto">
            {isSearching ? (
              <div className="p-4 text-center">
                <Loader2 className="w-5 h-5 animate-spin mx-auto text-emerald-500" />
                <p className="text-xs text-gray-500 mt-2">Searching...</p>
              </div>
            ) : error ? (
              <div className="p-4 text-center">
                <p className="text-xs text-red-400">{error}</p>
              </div>
            ) : searchResults.length > 0 ? (
              <div className="py-1">
                {searchResults.map((result) => (
                  <button
                    key={result.placeId}
                    onClick={() => handleSelectLocation(result)}
                    className="w-full px-3 py-2 text-left hover:bg-gray-700/50 transition-colors group"
                  >
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0 group-hover:text-emerald-400" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-200 truncate">
                          {result.name}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {result.address}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : searchQuery.trim() ? (
              <div className="p-4 text-center">
                <p className="text-xs text-gray-500">No locations found</p>
              </div>
            ) : (
              <div className="p-4 text-center">
                <p className="text-xs text-gray-500">Start typing to search</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
